import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  USER_ROLE_APARTMENT_LOCATOR,
} from 'common/userRoles';
import getLastPageVisitedByScreenIdList from 'common/util/getLastPageVisitedByScreenIdList';

import {
  DATA_TABLE_SOURCE_RENTER_FEED,
  DATA_TABLE_VIEW_ID_RENTER_FEED_LEAD,
} from 'common/dataTableConstants';

import {
  SCREEN_ID_APARTMENT_LOCATOR_FEED,
  SCREEN_ID_APARTMENT_LOCATOR_LEAD_ACTIVITY,
  SCREEN_ID_APARTMENT_LOCATOR_LEAD,
} from 'client/constants/screenIds';

import {
  dataTable,
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';

import H1 from 'client/components/H1';
import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import SrAgentLeadsActivityRow from 'client/components/SrAgentLeadsActivityRow';

const screenIdList = [
  SCREEN_ID_APARTMENT_LOCATOR_FEED,
  SCREEN_ID_APARTMENT_LOCATOR_LEAD,
];

function getDataTableSettingsFromProps({ leadUser }) {
  return {
    dataSource: DATA_TABLE_SOURCE_RENTER_FEED,
    viewId: `${DATA_TABLE_VIEW_ID_RENTER_FEED_LEAD}-${leadUser && leadUser.id || ''}`,
    sort: {
      'renterActivity.createdDate': 'desc',
    },
    filter: {
      'renterActivity.renterUserId': leadUser ? leadUser.id : null,
    },
  };
}

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_LEAD_ACTIVITY)
@withInfiniteScroll({
  offset: 32,
  savePositionWithId: SCREEN_ID_APARTMENT_LOCATOR_LEAD_ACTIVITY,
})
@branch({
  currentUser: ['currentUser'],
  leadUser: ['apartmentLocator', 'lead', 'user'],
  listingPhotoResolution: ['listingPhotoResolution'],
  screenIdHistory: ['analytics', 'screenIdHistory'],
  pathHistory: ['analytics', 'pathHistory'],
})
@dataTable(getDataTableSettingsFromProps)
class AgentLeadActivityPage extends Component {
  static fetchData(match, inject) {
    return Promise.all([
      ApartmentLocatorActions.getLead(inject, parseInt(match.params.renterUserId, 10)),
    ]);
  }

  static propTypes = {
    currentUser: PropTypes.object,
    listingPhotoResolution: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    leadUser: PropTypes.object,
    match: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    screenIdHistory: PropTypes.array.isRequired,
    pathHistory: PropTypes.array.isRequired,
  }

  static getPageMetadata = () => ({
    pageTitle: 'Lead Activity | Sunroom Rentals',
    section: 'Lead Activity',
  });

  componentDidMount() {
    const { onInfiniteScrollCallbackReady } = this.props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
    this._infiniteScrollCallback();
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch, items } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadItems, {
        count: 8,
        skip: items.length,
        ...getDataTableSettingsFromProps(this.props),
      });
    }
  }

  render() {
    const {
      currentUser,
      leadUser,
      listingPhotoResolution,
      items,
      dispatch,
      dataTableApiState,
      match,
      onInfiniteScrollWrapperRef,
      screenIdHistory,
      pathHistory,
    } = this.props;

    let backLinkUrl = `/agent/lead/${match.params.renterUserId}`;
    const lastPageVisited = getLastPageVisitedByScreenIdList({
      screenIdHistory,
      pathHistory,
      screenIdList,
    });

    if (lastPageVisited) {
      backLinkUrl = lastPageVisited.path;
    }

    return (
      <div
        className='page-agentDashboard lead'
        ref={onInfiniteScrollWrapperRef}
      >
        <Navbar
          redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })}
          leftButton={(
            <SrLink
              to={backLinkUrl}
              className='link back'
              restoreScrollPosition
            />
          )}
        />
        {leadUser && (
          <div className='contentContainer'>
            <div className='header'>
              <H1>{leadUser.name}'s Activity</H1>
            </div>
            <div className='leadActivityContainer'>
              {items.map((activity) => (
                <SrAgentLeadsActivityRow
                  activity={activity}
                  key={`${activity.activityType}-${activity.id}`}
                  dispatch={dispatch}
                  listingPhotoResolution={listingPhotoResolution}
                  source='leadActivity'
                />
              ))}
              {dataTableApiState.inProgress && (
                <div className='loading-gif clearfix' />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

AgentLeadActivityPage.displayName = 'AgentLeadActivityPage';

export default AgentLeadActivityPage;
